import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import registryService from '../services/registryService';
import { useDebouncedValue } from './useDebouncedValue';

const DEFAULT_ROWS = 10;
const LOAD_ERROR = 'Errore nel caricamento delle voci';

/** Ordinamento per attributo e filtri `f.<campo>` di GET /entries e dell'export (senza paginazione). */
export const buildEntryQuery = ({ filters, sort }) => {
  const query = {
    sort: sort ? `attributes.${sort.field},${sort.direction}` : 'createdAt,desc',
  };
  Object.entries(filters || {}).forEach(([field, value]) => {
    const term = typeof value === 'string' ? value.trim() : '';
    if (term) query[`f.${field}`] = term;
  });
  return query;
};

/** Parametri di GET /entries: paginazione piu' ordinamento e filtri. */
export const buildEntryParams = ({ filters, sort, page, rowsPerPage }) => ({
  page,
  size: rowsPerPage,
  ...buildEntryQuery({ filters, sort }),
});

/**
 * Stato e caricamento della tabella voci di un'anagrafica (ricerca per campo,
 * ordinamento, paginazione lato server). Stato locale, non nell'URL: vive in una dialog.
 */
const useRegistryEntries = (registry, { debounceMs = 300 } = {}) => {
  const registryId = registry?.id ?? null;
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(DEFAULT_ROWS);
  const [entries, setEntries] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [epoch, setEpoch] = useState(0);
  const requestId = useRef(0);

  // Cambio anagrafica: reset durante il render (non in un effect), cosi' la
  // richiesta per la nuova anagrafica non parte con filtri/sort/pagina della precedente.
  const [scopeId, setScopeId] = useState(registryId);
  if (scopeId !== registryId) {
    setScopeId(registryId);
    setEpoch((e) => e + 1);
    setFilters({});
    setSort(null);
    setPage(0);
    setEntries([]);
    setTotalElements(0);
    setError(null);
  }

  // Il valore debounced e' legato all'epoca (cambio anagrafica / clearFilters): un valore
  // di un'epoca precedente non deve mai finire in una richiesta; senza filtri niente debounce.
  const filtersKey = JSON.stringify(filters);
  const debouncedScoped = useDebouncedValue(`${epoch}|${filtersKey}`, debounceMs);
  const sep = debouncedScoped.indexOf('|');
  const debouncedEpoch = Number(debouncedScoped.slice(0, sep));
  const requestFilters = filtersKey === '{}' || debouncedEpoch !== epoch
    ? '{}'
    : debouncedScoped.slice(sep + 1);

  useEffect(() => {
    if (!registryId) {
      // invalida l'eventuale richiesta in volo dell'anagrafica precedente
      requestId.current += 1;
      setLoading(false);
      return undefined;
    }
    const id = ++requestId.current;
    setLoading(true);
    const params = buildEntryParams({ filters: JSON.parse(requestFilters), sort, page, rowsPerPage });
    Promise.resolve(registryService.getEntries(registryId, params))
      .then((data) => {
        if (id !== requestId.current) return;
        const content = Array.isArray(data?.content) ? data.content : [];
        const total = Number(data?.totalElements) || 0;
        if (content.length === 0 && page > 0 && total > 0) {
          // pagina oltre l'ultima (voci eliminate altrove): vai all'ultima pagina, il nuovo page riavvia l'effect;
          // se questa e' gia' l'ultima, la pagina vuota e il totale vanno comunque mostrati
          const last = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
          if (last !== page) {
            setPage(last);
            return;
          }
        }
        setEntries(content);
        setTotalElements(total);
        setError(null);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setEntries([]);
        setTotalElements(0);
        setError(LOAD_ERROR);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    return undefined;
  }, [registryId, requestFilters, sort, page, rowsPerPage, reloadToken]);

  const setFilter = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setEpoch((e) => e + 1);
    setFilters({});
    setPage(0);
  }, []);

  const toggleSort = useCallback((field) => {
    setSort((prev) => (prev && prev.field === field
      ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { field, direction: 'asc' }));
    setPage(0);
  }, []);

  const setRowsPerPage = useCallback((n) => {
    setRowsPerPageState(n);
    setPage(0);
  }, []);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  const appliedFilters = useMemo(() => JSON.parse(requestFilters), [requestFilters]);

  const hasActiveFilters = Object.values(filters).some((v) => typeof v === 'string' && v.trim() !== '');

  return {
    entries, totalElements, loading, error,
    // filters: quello che si sta digitando; appliedFilters: filtri dell'ultima richiesta, quello che la tabella mostra
    filters, appliedFilters, setFilter, clearFilters, hasActiveFilters,
    sort, toggleSort,
    page, setPage, rowsPerPage, setRowsPerPage,
    reload,
  };
};

export default useRegistryEntries;
